import { useEffect, useState } from 'react';
import { Navbar } from '../../components';
import { useScroll } from 'framer-motion';
import { motion } from 'framer-motion'
import { FADE_BOTTOM } from '../../animations';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useRouter } from 'next/router';
import Footer from '@/components/sections/Footer';
import SEO from '@/components/SEO';

export async function getStaticProps({ locale }: any) {
  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'common',
      ])),
    },
  }
}

export default function TermsConditions() {
  const [navbarTransparent, setNavbarTransparent] = useState(true)
  const { scrollYProgress, scrollY } = useScroll()
  const { t } = useTranslation();
  const router = useRouter();
  const { asPath: pathname } = router;

  const handleNavigateToPrivacy = () => {
    const currentLocale = router.locale;
    router.push(`/privacy_policy`, undefined, { locale: currentLocale });
  }

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    scrollYProgress.on('change', () => {
      if (scrollY.get() >= 100) {
        setNavbarTransparent(false)
      } else {
        setNavbarTransparent(true)
      }
    })
  }, [])

  const sectionAnimation = {
    initial: FADE_BOTTOM.initial,
    whileInView: FADE_BOTTOM.final,
    transition: { duration: 0.4, delay: 0.3 },
    viewport: { once: true },
  };

  return (
    <>
      <SEO
        title={`DeepNeuronic - ${t("Terms and conditions")}`}
        description={t("terms_p1")}
      />
      <div className="min-h-screen">
        <Navbar transparent={navbarTransparent} />

        {/* Hero Section */}
        <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-secondary">
            <div className="absolute inset-0 opacity-20">
              {[...Array(30)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1 h-1 bg-primary rounded-full animate-pulse"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 3}s`,
                    animationDuration: `${2 + Math.random() * 3}s`,
                  }}
                />
              ))}
            </div>
          </div>

          <div className="relative z-10 container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center animate-fade-up">
              {/* Badge */}
              <div className="inline-flex items-center justify-center mb-6">
                <div className="relative px-6 py-2 rounded-full bg-electric-blue/10 backdrop-blur-sm light_effect_rounded_full">
                  <span className="text-sm font-semibold tracking-wider uppercase text-electric-blue">
                    {t("Legal")}
                  </span>
                </div>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                {t("Terms and conditions")}
              </h1>
              <p className="text-xl md:text-2xl text-secondary-foreground leading-relaxed">
                DeepNeuronic
              </p>
            </div>
          </div>
        </section>

        {/* Tab Navigation */}
        <div className="border-b border-border">
          <div className="container mx-auto px-6">
            <div className="flex items-center justify-center gap-2">
              <div className="px-6 py-3 text-sm font-semibold text-electric-blue border-b-2 border-electric-blue cursor-default">
                {t("Terms and conditions")}
              </div>
              <div
                onClick={handleNavigateToPrivacy}
                className="px-6 py-3 text-sm font-semibold text-muted-foreground hover:text-foreground border-b-2 border-transparent hover:border-muted transition-colors cursor-pointer"
              >
                {t("Privacy Policy")}
              </div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto">
              {/* Title & Last Updated */}
              <div className="mb-12">
                <h2 className="text-3xl md:text-4xl font-bold mb-3">{t("Terms and conditions")}</h2>
                <p className="text-muted-foreground">Last updated: July 12, 2023</p>
              </div>

              {/* Introduction */}
              <motion.div {...sectionAnimation} className="mb-12">
                <p className="text-secondary-foreground leading-relaxed">
                  {t("terms_p1")}
                </p>
                <p className="mt-4 text-secondary-foreground leading-relaxed">
                  <strong className="text-foreground text-lg">{t("terms_p2_highlighted")} </strong>
                  {t("terms_p2")}
                </p>
              </motion.div>

              {/* Section: terms_t1 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("terms_t1")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("terms_p3")}</p>
              </motion.div>

              {/* Section: terms_t2 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("terms_t2")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("terms_p4")}</p>
              </motion.div>

              {/* Section: terms_t3 (with list) */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("terms_t3")}</h3>
                <p className="text-secondary-foreground leading-relaxed mb-4">{t("terms_i")}</p>
                <ul className="space-y-2 text-secondary-foreground leading-relaxed pl-1">
                  <li className="flex gap-2"><span className="text-muted-foreground">(a)</span> {t("terms_i1")}</li>
                  <li className="flex gap-2"><span className="text-muted-foreground">(b)</span> {t("terms_i2")}</li>
                  <li className="flex gap-2"><span className="text-muted-foreground">(c)</span> {t("terms_i3")}</li>
                  <li className="flex gap-2"><span className="text-muted-foreground">(d)</span> {t("terms_i4")}</li>
                  <li className="flex gap-2"><span className="text-muted-foreground">(e)</span> {t("terms_i5")}</li>
                  <li className="flex gap-2"><span className="text-muted-foreground">(f)</span> {t("terms_i6")}</li>
                  <li className="flex gap-2"><span className="text-muted-foreground">(g)</span> {t("terms_i7")}</li>
                  <li className="flex gap-2"><span className="text-muted-foreground">(h)</span> {t("terms_i8")}</li>
                  <li className="flex gap-2"><span className="text-muted-foreground">(i)</span> {t("terms_i9")}</li>
                  <li className="flex gap-2"><span className="text-muted-foreground">(j)</span> {t("terms_i10")}</li>
                </ul>
              </motion.div>

              {/* Section: terms_t4 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("terms_t4")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("terms_p5")}</p>
              </motion.div>

              {/* Section: terms_t5 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("terms_t5")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("terms_p6")}</p>
              </motion.div>

              {/* Section: terms_t6 */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("terms_t6")}</h3>
                <p className="text-secondary-foreground leading-relaxed">{t("terms_p7")}</p>
              </motion.div>

              {/* Section: Contact */}
              <motion.div {...sectionAnimation} className="mb-12">
                <h3 className="text-xl md:text-2xl font-bold mb-4">{t("terms_t7")}</h3>
                <p className="text-secondary-foreground leading-relaxed mb-4">{t("terms_p8")}</p>
                <div className="bg-card border border-border rounded-xl p-6">
                  <p className="text-secondary-foreground">
                    https://www.deepneuronic.com/contact/
                  </p>
                  <p className="text-muted-foreground mt-2">
                    UBIMEDICAL, Estrada Municipal 506, 6200-284 Covilha, Portugal
                  </p>
                </div>
              </motion.div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}
